'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import PhoneLink from '@/app/contact/PhoneLink';

const navItems = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services' },
  { label: 'Facility & Pressure Washing', href: '/facility-pressure-washing' },
  { label: 'Appliance Cleaning', href: '/commercial-appliance-cleaning-calgary' },
  { label: 'NFPA 96 Guide', href: '/nfpa-96-compliance-guide' },
  { label: 'Gallery', href: '/gallery' },
  { label: 'About', href: '/about' },
  { label: 'Contact', href: '/contact' },
  { label: 'Get a Quote', href: '/quote' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [bookingDropdownOpen, setBookingDropdownOpen] = useState(false);
  const [mobileBookingOpen, setMobileBookingOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setBookingDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <div style={{ height: '98px' }} aria-hidden="true" />
      <header className="w-full fixed top-0 left-0 right-0 z-50" style={{ zIndex: 50 }}>
        <div className="relative mx-auto max-w-screen-2xl px-6">
          <div className="relative mt-4 p-1 rounded-2xl">
            <div
              className="pointer-events-none absolute inset-0 rounded-2xl backdrop-blur-md"
              style={{ backgroundColor: 'rgba(28,38,45,0.82)', border: '1px solid rgba(55,73,212,0.25)' }} />

            <div className="flex w-full items-center gap-4 relative">
              {/* Mobile menu button + Logo */}
              <div className="flex h-full shrink-0 items-center gap-2 flex-1">
                <button
                  aria-label="Open navigation menu"
                  onClick={() => setMobileOpen(!mobileOpen)}
                  className="inline-flex shrink-0 cursor-pointer items-center justify-center bg-transparent border border-transparent rounded-2xl p-2 xl:hidden"
                  style={{ color: '#FBFCFD' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" width="24" height="24">
                    <path fill="none" d="M0 0h24v24H0z" />
                    <path d="M3 18h18v-2H3zm0-5h18v-2H3zm0-7v2h18V6z" />
                  </svg>
                </button>
                <Link href="/" aria-label="Home" className="flex items-center">
                  <Image
                    src="/assets/images/compressed-1778646282485.jpg"
                    alt="Northern Hood and Exhaust Logo"
                    width={200}
                    height={80}
                    className="h-8 w-auto"
                    priority
                    style={{ transform: 'scale(1.56)', transformOrigin: 'center left', mixBlendMode: 'multiply' }} />
                </Link>
              </div>

              {/* Desktop Nav */}
              <nav className="hidden xl:flex items-center gap-x-6 justify-center">
                {navItems?.map((item) => (
                  <Link key={item?.href} href={item?.href}>
                    <span className="py-2 font-source text-lg font-semibold text-[rgba(8,41,74,1)]" style={{ color: '#FBFCFD' }}>
                      {item?.label}
                    </span>
                  </Link>
                ))}

                {/* Book Appointment Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setBookingDropdownOpen((o) => !o)}
                    className="flex items-center gap-1 py-2 font-source text-lg font-semibold"
                    style={{ color: '#FBFCFD' }}>
                    Book Appointment
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      className={`transition-transform ${bookingDropdownOpen ? 'rotate-180' : ''}`}>
                      <path d="M7 10l5 5 5-5z" />
                    </svg>
                  </button>

                  {bookingDropdownOpen && (
                    <div
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 rounded-2xl border shadow-xl overflow-hidden z-50"
                      style={{ backgroundColor: '#1C262D', borderColor: 'rgba(55,73,212,0.35)' }}>
                      <Link
                        href="/book-new"
                        onClick={() => setBookingDropdownOpen(false)}
                        className="flex items-start gap-3 px-5 py-4 hover:bg-white/10 transition-colors border-b"
                        style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                        <span className="text-xl mt-0.5">🆕</span>
                        <div>
                          <p className="font-bold text-sm" style={{ color: '#FBFCFD' }}>New Customer</p>
                          <p className="text-xs mt-0.5" style={{ color: '#c8d8e8' }}>Book a FREE inspection — no cost, no commitment</p>
                        </div>
                      </Link>
                      <Link
                        href="/book-returning"
                        onClick={() => setBookingDropdownOpen(false)}
                        className="flex items-start gap-3 px-5 py-4 hover:bg-white/10 transition-colors">
                        <span className="text-xl mt-0.5">🔧</span>
                        <div>
                          <p className="font-bold text-sm" style={{ color: '#FBFCFD' }}>Existing Customer</p>
                          <p className="text-xs mt-0.5" style={{ color: '#c8d8e8' }}>Schedule your next service with your account number</p>
                        </div>
                      </Link>
                    </div>
                  )}
                </div>
              </nav>

              {/* Social + CTA */}
              <div className="flex grow items-center justify-end gap-4 flex-1">
                <div className="items-center gap-3 hidden xl:flex">
                  <a href="https://www.facebook.com/northernhoodandexhaust" target="_blank" rel="noopener noreferrer" aria-label="Northern Hood and Exhaust on Facebook">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="20" height="20" style={{ color: '#FBFCFD' }}>
                      <path fill="currentColor" fillRule="evenodd" d="M10.438 21.878C5.659 21.128 2 16.988 2 12 2 6.48 6.48 2 12 2s10 4.48 10 10c0 4.988-3.66 9.127-8.437 9.878v-6.987h2.33L16.336 12h-2.773v-1.876c0-.79.387-1.562 1.63-1.562h1.26v-2.46s-1.144-.196-2.238-.196c-2.285 0-3.777 1.385-3.777 3.89V12h-2.54v2.89h2.54z" clipRule="evenodd" />
                    </svg>
                  </a>
                  <a href="https://www.instagram.com/northernhoodandexhaust" target="_blank" rel="noopener noreferrer" aria-label="Northern Hood and Exhaust on Instagram">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="20" height="20" style={{ color: '#FBFCFD' }}>
                      <path fill="currentColor" fillRule="evenodd" d="M12 2c5.52 0 10 4.48 10 10s-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2m0 3.75c-1.697 0-1.91.007-2.577.038-.665.03-1.12.136-1.517.29-.411.16-.76.374-1.107.721a3.1 3.1 0 0 0-.72 1.107c-.155.398-.261.852-.291 1.517-.03.667-.038.88-.038 2.577s.007 1.91.038 2.577c.03.665.136 1.12.29 1.517.16.411.374.76.721 1.107.347.348.696.561 1.107.72.398.155.852.261 1.517.291.667.03.88.038 2.577.038s1.91-.007 2.577-.038c.665-.03 1.12-.136 1.517-.29.411-.16.76-.373 1.107-.721.348-.348.561-.696.72-1.107.155-.398.261-.852.291-1.517.03-.667.038-.88.038-2.577s-.007-1.91-.038-2.577c-.03-.665-.136-1.12-.29-1.517a3.1 3.1 0 0 0-.721-1.107 3.1 3.1 0 0 0-1.107-.72c-.398-.155-.852-.261-1.517-.291-.667-.03-.88-.038-2.577-.038m0 1.126c1.669 0 1.867.006 2.525.037.61.027.94.13 1.161.215.292.113.5.249.719.467s.354.427.467.719c.086.22.188.551.215 1.16.03.66.037.857.037 2.526s-.006 1.867-.037 2.525c-.027.61-.13.94-.215 1.161-.113.292-.249.5-.467.719a1.9 1.9 0 0 1-.719.467c-.22.086-.551.188-1.16.215-.66.03-.857.037-2.526.037s-1.867-.006-2.526-.037c-.609-.027-.94-.13-1.16-.215a1.9 1.9 0 0 1-.719-.467 1.9 1.9 0 0 1-.467-.719c-.086-.22-.188-.551-.215-1.16-.03-.66-.037-.857-.037-2.526s.006-1.867.037-2.526c.027-.609.13-.94.215-1.16.113-.292.249-.5.467-.719s.427-.354.719-.467c.22-.086.551-.188 1.16-.215.66-.03.857-.037 2.526-.037m0 1.915a3.21 3.21 0 1 0 0 6.419 3.21 3.21 0 0 0 0-6.42m0 5.292a2.083 2.083 0 1 1 0-4.166 2.083 2.083 0 0 1 0 4.166m4.086-5.42a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0" clipRule="evenodd" />
                    </svg>
                  </a>
                  <a href="https://www.linkedin.com/company/northern-hood-and-exhaust" target="_blank" rel="noopener noreferrer" aria-label="Northern Hood and Exhaust on LinkedIn">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="20" height="20" style={{ color: '#FBFCFD' }}>
                      <path fill="currentColor" fillRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m-1.75 14.5H8.5v-7h1.75zm-.875-8.05a1.025 1.025 0 1 1 0-2.05 1.025 1.025 0 0 1 0 2.05M16.5 16.5h-1.75v-3.4c0-.812-.688-1.475-1.5-1.475s-1.5.663-1.5 1.475v3.4H10v-7h1.75v1.05c.475-.7 1.4-1.175 2.25-1.175 1.375 0 2.5 1.125 2.5 2.5z" clipRule="evenodd" />
                    </svg>
                  </a>
                </div>
                <PhoneLink
                  source="header — desktop"
                  className="inline-flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap transition-all rounded-2xl border px-4 py-2 font-semibold text-[17px]"
                  style={{ backgroundColor: '#c94e1a', borderColor: '#c94e1a', color: '#ffffff' }}>
                  Call to book the service
                </PhoneLink>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="xl:hidden absolute top-full left-0 right-0 mx-6 mt-1 rounded-2xl border p-4" style={{ backgroundColor: '#1C262D', borderColor: 'rgba(55,73,212,0.35)' }}>
            <nav className="flex flex-col gap-2">
              {navItems?.map((item) => (
                <Link
                  key={item?.href}
                  href={item?.href}
                  onClick={() => setMobileOpen(false)}
                  className="py-2 px-4 rounded-xl text-sm font-source transition-colors hover:bg-white/10"
                  style={{ color: '#FBFCFD' }}>
                  {item?.label}
                </Link>
              ))}

              {/* Mobile Book Appointment Accordion */}
              <div>
                <button
                  onClick={() => setMobileBookingOpen((o) => !o)}
                  className="w-full flex items-center justify-between py-2 px-4 rounded-xl text-sm font-source font-semibold transition-colors"
                  style={{ color: '#FBFCFD' }}>
                  Book Appointment
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    className={`transition-transform ${mobileBookingOpen ? 'rotate-180' : ''}`}>
                    <path d="M7 10l5 5 5-5z" />
                  </svg>
                </button>
                {mobileBookingOpen && (
                  <div className="ml-4 mt-1 flex flex-col gap-1">
                    <Link
                      href="/book-new"
                      onClick={() => { setMobileOpen(false); setMobileBookingOpen(false); }}
                      className="flex items-center gap-2 py-2.5 px-4 rounded-xl text-sm transition-colors"
                      style={{ backgroundColor: 'rgba(55,73,212,0.2)', color: '#FBFCFD' }}>
                      <span>🆕</span>
                      <div>
                        <p className="font-bold text-sm">New Customer</p>
                        <p className="text-xs opacity-75">FREE Inspection</p>
                      </div>
                    </Link>
                    <Link
                      href="/book-returning"
                      onClick={() => { setMobileOpen(false); setMobileBookingOpen(false); }}
                      className="flex items-center gap-2 py-2.5 px-4 rounded-xl text-sm transition-colors"
                      style={{ backgroundColor: 'rgba(0,74,173,0.2)', color: '#FBFCFD' }}>
                      <span>🔧</span>
                      <div>
                        <p className="font-bold text-sm">Existing Customer</p>
                        <p className="text-xs opacity-75">Schedule Service</p>
                      </div>
                    </Link>
                  </div>
                )}
              </div>

              <PhoneLink
                source="header — mobile menu"
                className="mt-2 inline-flex items-center justify-center rounded-2xl border px-4 py-2 text-sm font-semibold"
                style={{ backgroundColor: '#c94e1a', borderColor: '#c94e1a', color: '#ffffff' }}>
                Call to book the service
              </PhoneLink>
              {/* Mobile Social Icons */}
              <div className="flex items-center justify-center gap-5 mt-3 pt-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                <a href="https://www.facebook.com/northernhoodandexhaust" target="_blank" rel="noopener noreferrer" aria-label="Northern Hood and Exhaust on Facebook">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="24" height="24" style={{ color: '#FBFCFD' }}>
                    <path fill="currentColor" fillRule="evenodd" d="M10.438 21.878C5.659 21.128 2 16.988 2 12 2 6.48 6.48 2 12 2s10 4.48 10 10c0 4.988-3.66 9.127-8.437 9.878v-6.987h2.33L16.336 12h-2.773v-1.876c0-.79.387-1.562 1.63-1.562h1.26v-2.46s-1.144-.196-2.238-.196c-2.285 0-3.777 1.385-3.777 3.89V12h-2.54v2.89h2.54z" clipRule="evenodd" />
                  </svg>
                </a>
                <a href="https://www.instagram.com/northernhoodandexhaust" target="_blank" rel="noopener noreferrer" aria-label="Northern Hood and Exhaust on Instagram">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="24" height="24" style={{ color: '#FBFCFD' }}>
                    <path fill="currentColor" fillRule="evenodd" d="M12 2c5.52 0 10 4.48 10 10s-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2m0 3.75c-1.697 0-1.91.007-2.577.038-.665.03-1.12.136-1.517.29-.411.16-.76.374-1.107.721a3.1 3.1 0 0 0-.72 1.107c-.155.398-.261.852-.291 1.517-.03.667-.038.88-.038 2.577s.007 1.91.038 2.577c.03.665.136 1.12.29 1.517.16.411.374.76.721 1.107.347.348.696.561 1.107.72.398.155.852.261 1.517.291.667.03.88.038 2.577.038s1.91-.007 2.577-.038c.665-.03 1.12-.136 1.517-.29.411-.16.76-.373 1.107-.721.348-.348.561-.696.72-1.107.155-.398.261-.852.291-1.517.03-.667.038-.88.038-2.577s-.007-1.91-.038-2.577c-.03-.665-.136-1.12-.29-1.517a3.1 3.1 0 0 0-.721-1.107 3.1 3.1 0 0 0-1.107-.72c-.398-.155-.852-.261-1.517-.291-.667-.03-.88-.038-2.577-.038m0 1.126c1.669 0 1.867.006 2.525.037.61.027.94.13 1.161.215.292.113.5.249.719.467s.354.427.467.719c.086.22.188.551.215 1.16.03.66.037.857.037 2.526s-.006 1.867-.037 2.525c-.027.61-.13.94-.215 1.161-.113.292-.249.5-.467.719a1.9 1.9 0 0 1-.719.467c-.22.086-.551.188-1.16.215-.66.03-.857.037-2.526.037s-1.867-.006-2.526-.037c-.609-.027-.94-.13-1.16-.215a1.9 1.9 0 0 1-.719-.467 1.9 1.9 0 0 1-.467-.719c-.086-.22-.188-.551-.215-1.16-.03-.66-.037-.857-.037-2.526s.006-1.867.037-2.526c.027-.609.13-.94.215-1.16.113-.292.249-.5.467-.719s.427-.354.719-.467c.22-.086.551-.188 1.16-.215.66-.03.857-.037 2.526-.037m0 1.915a3.21 3.21 0 1 0 0 6.419 3.21 3.21 0 0 0 0-6.42m0 5.292a2.083 2.083 0 1 1 0-4.166 2.083 2.083 0 0 1 0 4.166m4.086-5.42a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0" clipRule="evenodd" />
                  </svg>
                </a>
                <a href="https://www.linkedin.com/company/northern-hood-and-exhaust" target="_blank" rel="noopener noreferrer" aria-label="Northern Hood and Exhaust on LinkedIn">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="24" height="24" style={{ color: '#FBFCFD' }}>
                    <path fill="currentColor" fillRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m-1.75 14.5H8.5v-7h1.75zm-.875-8.05a1.025 1.025 0 1 1 0-2.05 1.025 1.025 0 0 1 0 2.05M16.5 16.5h-1.75v-3.4c0-.812-.688-1.475-1.5-1.475s-1.5.663-1.5 1.475v3.4H10v-7h1.75v1.05c.475-.7 1.4-1.175 2.25-1.175 1.375 0 2.5 1.125 2.5 2.5z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
